import React from "react";
import { useTranslation } from "react-i18next";
import PropTypes from "prop-types";
import decodeHTMLEntities from "core-web/utils/decodeHTMLEntities";
import AspectWrapper from "core-web/components/AspectWrapper";
import BackgroundImage from "core-web/components/BackgroundImage";
import Link from "core-web/components/Link";
import { PLACEHOLDER } from "theme/config/constants";
import styled from "theme/libs/styled";
import ThemeButton from "theme/components/buttons/ThemeButton";
import ArrowIcon from "theme/components/icons/ArrowIcon";
import TextStyles from "theme/components/text/TextStyles";

const Wrapper = styled("div")``;

const ImageWrapper = styled("div")`
    border-radius: 12px;
    overflow: hidden;
    ${({ theme }) => theme.above.tabletLg} {
        .background {
            transition: transform ${({ theme }) => theme.transitions.primarySlow};
        }
        &:hover .background {
            transform: scale(1.05);
        }
    }
`;

const Content = styled("div")`
    display: flex;
    flex-direction: column;
    margin-top: 12px;
    gap: 6px;

    ${({ theme }) => theme.above.tabletLg} {
        gap: 8px;
    }
`;

const Metadata = styled("div")`
    display: flex;
    flex-wrap: wrap;
    gap: 4px;
    opacity: 0.6;
`;

const PreviewTextWrapper = styled("div")`
    min-height: 2.4em;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
`;

const ArticleCard = ({
    background,
    created,
    heading,
    category,
    link,
    loading,
    headingTextStyles,
    previewText,
    publishedAgo,
}) => {
    const { t } = useTranslation();
    const desktopImage = background?.mobile?.url;
    const mobileImage = background?.desktop?.url;
    const metadataParts = [created, category, publishedAgo].filter(Boolean);

    return (
        <Wrapper>
            <ImageWrapper>
                <Link to={link}>
                    <AspectWrapper ratio={4 / 3}>
                        <BackgroundImage
                            className="background"
                            src={[
                                mobileImage || desktopImage || PLACEHOLDER,
                                null,
                                null,
                                desktopImage || mobileImage || PLACEHOLDER,
                            ]}
                            loading={loading}
                            query={{
                                w: [360, null, null, 420, 600],
                                q: 70,
                            }}
                        />
                    </AspectWrapper>
                </Link>
            </ImageWrapper>
            <Content>
                {metadataParts.length > 0 && (
                    <Metadata>
                        <TextStyles textStyles="Myriad Pro/14-140">{metadataParts.join(" | ")}</TextStyles>
                    </Metadata>
                )}
                {heading && (
                    <Link to={link}>
                        <TextStyles as="h2" textStyles={headingTextStyles}>
                            {decodeHTMLEntities(heading)}
                        </TextStyles>
                    </Link>
                )}
                {previewText && (
                    <PreviewTextWrapper>
                        <TextStyles textStyles="Myriad Pro/16-120">{previewText}</TextStyles>
                    </PreviewTextWrapper>
                )}
                <ThemeButton
                    mt={["8px", null, null, null, "12px"]}
                    as={Link}
                    to={link}
                    buttonTheme="black_outlined"
                    size="lg"
                    width="fit-content"
                >
                    {t("articles.read_more")}
                    <ArrowIcon direction="right" />
                </ThemeButton>
            </Content>
        </Wrapper>
    );
};

ArticleCard.propTypes = {
    background: PropTypes.shape({
        desktop: {
            url: PropTypes.string,
        },
        mobile: {
            url: PropTypes.string,
        },
    }),
    category: PropTypes.string,
    created: PropTypes.string,
    heading: PropTypes.string,
    headingTextStyles: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
    link: PropTypes.string,
    loading: PropTypes.string,
    previewText: PropTypes.string,
    publishedAgo: PropTypes.string,
};

ArticleCard.defaultProps = {
    background: {},
    category: null,
    created: null,
    heading: null,
    headingTextStyles: [
        "Buenos Aires/12-120 SemiBold",
        null,
        "Buenos Aires/16-120 SemiBold",
        null,
        "Buenos Aires/24-110 SemiBold",
    ],
    link: null,
    loading: "lazy",
    previewText: "",
    publishedAgo: null,
};

export default ArticleCard;
